import React, { useEffect, useState } from "react";

import {
  Box,
  Grid,
  Typography,
  ButtonBase,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";
import DescriptionIcon from "@material-ui/icons/Description";
import PeopleIcon from "@material-ui/icons/People";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import LabelIcon from "@material-ui/icons/Label";
import BuildIcon from "@material-ui/icons/Build";
import SendIcon from "@material-ui/icons/Send";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import ReceiptIcon from "@material-ui/icons/Receipt";

import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ScheduleIcon from "@material-ui/icons/Schedule";
import AssignmentIcon from "@material-ui/icons/Assignment";

import { useNavigate } from "react-router-dom";

import { InvoiceSummaryStats } from "src/adminModel/invoice";

/* ============================================================
   SHORTCUTS
============================================================ */

const SECTIONS = [
  {
    heading: "Lots & invoices",
    items: [
      { label: "Find lots", icon: SearchIcon, path: "/app/find/lot" },
      { label: "Invoices", icon: DescriptionIcon, path: "/app/invoice" },
      {
        label: "Receipt & certificate",
        icon: ReceiptIcon,
        path: "/app/receipt/certificate/invoice/create",
      },
    ],
  },
  {
    heading: "Reports",
    items: [
      {
        label: "Consignment",
        icon: LocalShippingIcon,
        path: "/app/report/consignment",
      },
      {
        label: "Commission",
        icon: MonetizationOnIcon,
        path: "/app/report/commission",
      },
      {
        label: "Certificate",
        icon: VerifiedUserIcon,
        path: "/app/report/certificate",
      },
      { label: "Sticker", icon: LabelIcon, path: "/app/report/sticker" },
      { label: "Releasing", icon: SendIcon, path: "/app/report/releasing" },
      { label: "Custom report", icon: BuildIcon, path: "/app/report/custom" },
    ],
  },
  {
    heading: "People",
    items: [
      { label: "User management", icon: PeopleIcon, path: "/app/user/list" },
    ],
  },
];

const ACCENT = "#3e4396";

/* ============================================================
   STYLES
============================================================ */

const useStyles = makeStyles((theme) => ({
  root: { width: "100%" },

  /* ===== STAT CARDS ===== */

  statCard: {
    width: "100%",
    minHeight: 125,
    backgroundColor: "#fff",
    border: "1px solid #e8e8ed",
    borderRadius: 10,
    padding: theme.spacing(2.2),
    boxSizing: "border-box",
    boxShadow: "0 2px 7px rgba(0,0,0,0.03)",
    // No hover lift. These cards are not clickable -- a card that moves
    // under the cursor promises an action that is not there.
  },

  statTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },

  statIconBox: {
    width: 42,
    height: 42,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  statIcon: { fontSize: 22 },

  statLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "#888",
    marginBottom: 5,
  },

  statValue: {
    fontSize: 24,
    fontWeight: 700,
    color: "#202020",
    lineHeight: 1.1,
  },

  statSub: { marginTop: 7, fontSize: 11, color: "#999" },

  /* ===== MONEY SUMMARY ===== */

  moneyCard: {
    width: "100%",
    backgroundColor: "#fff",
    border: "1px solid #e8e8ed",
    borderRadius: 10,
    padding: theme.spacing(2),
    boxSizing: "border-box",
  },

  moneyLabel: { fontSize: 11, color: "#888", marginBottom: 5 },

  moneyValue: { fontSize: 18, fontWeight: 700, color: "#222" },

  /* ===== NOTES ===== */

  note: {
    marginTop: theme.spacing(1),
    fontSize: 11,
    color: "#999",
  },

  /* ===== SECTIONS ===== */

  section: {
    marginBottom: theme.spacing(4),
    "&:last-child": { marginBottom: 0 },
  },

  heading: {
    marginBottom: theme.spacing(1.5),
    color: "#5b5b6b",
    fontWeight: 600,
  },

  /* ===== SHORTCUT ITEMS ===== */

  item: {
    width: "100%",
    height: 64,
    justifyContent: "flex-start",
    padding: theme.spacing(0, 2),
    backgroundColor: "#ffffff",
    border: "1px solid #e2e2e8",
    borderRadius: 6,
    transition: theme.transitions.create(
      ["border-color", "background-color"],
      { duration: 150 }
    ),
    "&:hover": {
      borderColor: ACCENT,
      backgroundColor: "#fafaff",
    },
    "&:focus-visible": {
      outline: `2px solid ${ACCENT}`,
      outlineOffset: 1,
    },
  },

  iconBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    flexShrink: 0,
    marginRight: theme.spacing(1.75),
    borderRadius: 4,
    backgroundColor: "rgba(62, 67, 150, 0.08)",
    color: ACCENT,
  },

  icon: { fontSize: 20 },

  label: { fontWeight: 500, textAlign: "left", lineHeight: 1.25 },

  /* ===== LOADING / ERROR ===== */

  loadingBox: {
    minHeight: 125,
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#fff",
    border: "1px solid #e8e8ed",
    borderRadius: 10,
  },

  errorBox: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: "1px solid #f5c6c6",
    borderRadius: 8,
    backgroundColor: "#fdf3f3",
    color: "#b3261e",
    fontSize: 13,
  },
}));

/* ============================================================
   HELPERS
============================================================ */

const money = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const EMPTY_STATS = {
  total_invoices: 0,
  paid_count: 0,
  unpaid_count: 0,
  partial_count: 0,
  cancelled_count: 0,
  total_amount: 0,
  total_collected: 0,
  outstanding_balance: 0,
};

/* ============================================================
   COMPONENT
============================================================ */

const DashboardShortcuts = () => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ============================================================
     LOAD SUMMARY

     One request. The database does the counting.

     The old version called AuctionListLimit(), then one InvoiceFindAll
     per auction, and downloaded EVERY invoice row just to count them in
     the browser. With 30 auctions and 500 invoices each that is 31
     requests and 15,000 rows over the wire to display seven numbers --
     and it grows every time an invoice is created.
  ============================================================ */

  useEffect(() => {
    let mounted = true;

    const loadSummary = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await InvoiceSummaryStats();

        if (!mounted) {
          return;
        }

        if (result?.S && result?.data) {
          setStats({ ...EMPTY_STATS, ...result.data });
        } else {
          // Say so instead of showing zeros. Zeros look like real data,
          // and "we have no invoices" is a very different message from
          // "we could not reach the server".
          setStats(EMPTY_STATS);
          setError(result?.errors || "Unable to load the invoice summary.");
        }
      } catch (err) {
        console.error("Dashboard Error:", err);

        if (mounted) {
          setStats(EMPTY_STATS);
          setError("Unable to load the invoice summary.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadSummary();

    return () => {
      mounted = false;
    };
  }, []);

  /* ============================================================
     STAT CARDS
  ============================================================ */

  const statCards = [
    {
      label: "Total Invoices",
      value: stats.total_invoices,
      sub: "All created invoices",
      icon: AssignmentIcon,
      iconBackground: "rgba(62,67,150,0.10)",
      iconColor: "#3e4396",
    },
    {
      label: "Paid",
      value: stats.paid_count,
      sub: "Fully paid invoices",
      icon: CheckCircleIcon,
      iconBackground: "rgba(46,125,50,0.10)",
      iconColor: "#2e7d32",
    },
    {
      label: "Unpaid",
      value: stats.unpaid_count,
      sub: "No payment recorded",
      icon: ScheduleIcon,
      iconBackground: "rgba(239,108,0,0.10)",
      iconColor: "#ef6c00",
    },
    {
      label: "Partial",
      value: stats.partial_count,
      sub: "Partially paid invoices",
      icon: AccountBalanceWalletIcon,
      iconBackground: "rgba(245,124,0,0.10)",
      iconColor: "#f57c00",
    },
  ];

  const moneyCards = [
    {
      label: "Total Invoice Amount",
      value: stats.total_amount,
      color: "#222",
    },
    {
      label: "Total Collected",
      value: stats.total_collected,
      color: "#2e7d32",
    },
    {
      label: "Outstanding Balance",
      value: stats.outstanding_balance,
      color: "#c62828",
    },
  ];

  return (
    <Box className={classes.root}>
      {error && <Box className={classes.errorBox}>{error}</Box>}

      {/* ===== INVOICE STATUS CARDS ===== */}

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {statCards.map(
          ({ label, value, sub, icon: Icon, iconBackground, iconColor }) => (
            <Grid item xs={12} sm={6} md={3} key={label}>
              {loading ? (
                <Box className={classes.loadingBox}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box className={classes.statCard}>
                  <Box className={classes.statTop}>
                    <Box>
                      <Typography className={classes.statLabel}>
                        {label}
                      </Typography>

                      <Typography className={classes.statValue}>
                        {Number(value || 0).toLocaleString()}
                      </Typography>

                      <Typography className={classes.statSub}>
                        {sub}
                      </Typography>
                    </Box>

                    <Box
                      className={classes.statIconBox}
                      style={{
                        backgroundColor: iconBackground,
                        color: iconColor,
                      }}
                    >
                      <Icon className={classes.statIcon} />
                    </Box>
                  </Box>
                </Box>
              )}
            </Grid>
          )
        )}
      </Grid>

      {/* ===== FINANCIAL SUMMARY ===== */}

      <Grid container spacing={2}>
        {moneyCards.map(({ label, value, color }) => (
          <Grid item xs={12} sm={4} key={label}>
            {loading ? (
              <Box className={classes.loadingBox} style={{ minHeight: 80 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Box className={classes.moneyCard}>
                <Typography className={classes.moneyLabel}>{label}</Typography>

                <Typography className={classes.moneyValue} style={{ color }}>
                  &#8369; {money(value)}
                </Typography>
              </Box>
            )}
          </Grid>
        ))}
      </Grid>

      {/*
        Cancelled invoices are left out of every figure above -- they are
        not receivables. Said out loud so nobody has to work out why the
        totals do not match the invoice list.
      */}
      {!loading && stats.cancelled_count > 0 && (
        <Typography className={classes.note}>
          {stats.cancelled_count} cancelled invoice
          {stats.cancelled_count === 1 ? " is" : "s are"} excluded from these
          figures.
        </Typography>
      )}

      <Box mt={4}>
        {/* ===== SHORTCUTS ===== */}

        {SECTIONS.map((section) => (
          <Box key={section.heading} className={classes.section}>
            <Typography variant="subtitle2" className={classes.heading}>
              {section.heading}
            </Typography>

            <Grid container spacing={2}>
              {section.items.map(({ label, icon: Icon, path }) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={path}>
                  <ButtonBase
                    className={classes.item}
                    onClick={() => navigate(path)}
                  >
                    <span className={classes.iconBox}>
                      <Icon className={classes.icon} />
                    </span>

                    <Typography variant="body2" className={classes.label}>
                      {label}
                    </Typography>
                  </ButtonBase>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default DashboardShortcuts;